import React, {useCallback, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  IntersperseItem,
  intersperseNodes,
} from '../../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {isDefined} from '../../../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {useAppQuery} from '../../../../../../../libs/shared/core/src/services/query/useAppQuery';
import {DropdownBoxCoordinator} from '../../../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {DropdownLoader} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdownLoader';
import {DropdownItemCheckbox} from '../../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemCheckbox';
import {SearchDropdown} from '../../../../../../../libs/shared/ui/src/components/dropdowns/search/searchDropdown';
import {PopoverTransferProps} from '../../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {ellipsis} from '../../../../../../../libs/shared/ui/src/styles/mixins';
import {fuzzySearchBuilder} from '../../../../../../helpers/fuzzySearchHelpers';
import {SafetyNetModel, SafetyNetRequiredTaggingModel} from '../../../../../../models/api/safetyNetModel';
import {TagModel} from '../../../../../../models/api/tagModel';
import {safetyNetQueries} from '../../../../../../queries/safetyNetsQueries';
import {DropdownItemTagIcon} from '../../../../../common/dropdowns/dropdownItemTagIcon';
import {TagDropdownItem} from '../../../../../common/dropdowns/tagDropdownItem';

/*
 * Consts.
 */

const MAX_TAGS_TO_RENDER = 3;

/*
 * Props.
 */

interface RequiredTagListProps {
  safetyNet: SafetyNetRequiredTaggingModel;
  selectedTagIds: ReadonlyArray<number>;
  onAddTag: (arg: number) => void;
  onRemoveTag: (arg: number) => void;
}

/*
 * Styles.
 */

const StyledSelectedTagContainerDiv = styled.div`
  display: flex;
  align-items: center;
`;

const StyledSelectedTagItemDiv = styled.div`
  display: flex;
  align-items: center;
  margin-right: 5px;
  overflow: hidden;
`;

const StyledSelectedTagItemTextDiv = styled.div`
  ${ellipsis()}
`;

const StyledCommaSeparatorSpan = styled.span`
  color: ${(p) => p.theme.palette.grey.shade40};
`;

const StyledExtraTagsNumberDiv = styled.div`
  color: ${(p) => p.theme.greys.shade60};
`;

/*
 * Component.
 */

export const RequiredTagList: React.FC<RequiredTagListProps> = (props) => {
  const {safetyNet, selectedTagIds, onAddTag, onRemoveTag} = props;
  const {isOneTagRequired, name: tagListName} = safetyNet;

  const {tags, onRetry, loadingStatus} = useSafetyNetTags(safetyNet);
  const fuzzySearch = useMemo(() => fuzzySearchBuilder<TagModel>((tag) => tag.name), []);

  const handleSelectTag = useCallback(
    (selectedTagId: number) => {
      if (selectedTagIds.includes(selectedTagId)) {
        return onRemoveTag(selectedTagId);
      }

      return onAddTag(selectedTagId);
    },
    [onAddTag, onRemoveTag, selectedTagIds],
  );

  const selectedTags = useMemo(
    () =>
      selectedTagIds.map((selectedTagId) => tags?.find((tag) => tag.id === selectedTagId)).filter(isDefined),
    [tags, selectedTagIds],
  );
  const [query, setQuery] = useState('');
  const renderRow = (tag: TagModel) => {
    const isChecked = selectedTagIds.includes(tag.id);
    return (
      <TagDropdownItem
        tagsPath={[]}
        tag={tag}
        nestingLevel={0}
        onSelect={(selectEvent) => {
          if (!isOneTagRequired) {
            selectEvent.preventDefault();
          }
          handleSelectTag(tag.id);
        }}
        isChecked={isChecked}
        renderRightContent={
          !isOneTagRequired ? () => <DropdownItemCheckbox isChecked={isChecked} /> : undefined
        }
      />
    );
  };
  const filteredData = useMemo(
    () => (query === '' ? tags : fuzzySearch(tags, query)), // This allow to keep the initial order.
    [fuzzySearch, query, tags],
  );

  const renderPopover = (popoverProps: PopoverTransferProps) => {
    if (loadingStatus !== AsyncStatusesEnum.LOADED) {
      // eslint-disable-next-line react/jsx-props-no-spreading
      return <DropdownLoader {...popoverProps} status={loadingStatus} onRetryClick={onRetry} />;
    }

    return (
      <SearchDropdown
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...popoverProps}
        data={filteredData}
        query={query}
        onQueryChange={setQuery}
        renderRow={renderRow}
        headerTitle={tagListName}
        width={380}
      />
    );
  };

  return (
    <DropdownBoxCoordinator
      label={tagListName}
      renderDropdown={renderPopover}
      value={renderSelectedTags(selectedTags)}
      isRequired
    />
  );
};

/*
 * Helpers.
 */

function renderSelectedTags(tags: ReadonlyArray<TagModel>) {
  if (tags.length === 0) {
    return <Trans id="required-tagging-dialog-default-tags-input">Select tags</Trans>;
  }

  const displayedTags = tags.slice(0, MAX_TAGS_TO_RENDER);
  const extraTagsNumber = tags.slice(MAX_TAGS_TO_RENDER).length;

  const nodes = displayedTags.map(
    (tag): IntersperseItem => ({
      node: (
        <StyledSelectedTagItemDiv>
          <DropdownItemTagIcon tag={tag} nestingLevel={0} />
          <StyledSelectedTagItemTextDiv>{tag.name}</StyledSelectedTagItemTextDiv>
        </StyledSelectedTagItemDiv>
      ),
      key: String(tag.id),
    }),
  );

  return (
    <StyledSelectedTagContainerDiv>
      {intersperseNodes(nodes, () => (
        <StyledCommaSeparatorSpan>,</StyledCommaSeparatorSpan>
      ))}
      {extraTagsNumber ? <StyledExtraTagsNumberDiv>+{extraTagsNumber}</StyledExtraTagsNumberDiv> : null}
    </StyledSelectedTagContainerDiv>
  );
}

function useSafetyNetTags(safetyNet: SafetyNetModel) {
  const {
    data: tags = [],
    asyncStatus,
    refetch,
  } = useAppQuery(safetyNetQueries.requiredTaggingDependencies(safetyNet.id));

  return {tags, loadingStatus: asyncStatus, onRetry: refetch};
}
